import type { CutleryType } from './CutlerySVGs'
import type { Difficulty } from './useCutlery'
import { shuffle } from '../utils'

export interface DishDef {
  id:       string
  emoji:    string
  requires: CutleryType[]
  es:       string
  en:       string
}

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

// ── Dish pool ────────────────────────────────────────────────────
const soups: DishDef[] = [
  { id: 'sopa-tomate',    emoji: '🍅', requires: ['spoon'],        es: 'Sopa de tomate',        en: 'Tomato soup'          },
  { id: 'gazpacho',       emoji: '🥣', requires: ['spoon'],        es: 'Gazpacho andaluz',      en: 'Andalusian gazpacho'  },
  { id: 'crema-verduras', emoji: '🥦', requires: ['spoon'],        es: 'Crema de verduras',     en: 'Cream of vegetables'  },
  { id: 'caldo',          emoji: '🍲', requires: ['spoon'],        es: 'Caldo de pollo',        en: 'Chicken broth'        },
]

const pasta: DishDef[] = [
  { id: 'espaguetis',  emoji: '🍝', requires: ['fork'],       es: 'Espaguetis carbonara',  en: 'Carbonara pasta'      },
  { id: 'ensalada',    emoji: '🥗', requires: ['fork'],       es: 'Ensalada mixta',        en: 'Mixed salad'          },
  { id: 'macarrones',  emoji: '🍝', requires: ['fork'],       es: 'Macarrones al tomate',  en: 'Pasta in tomato sauce'},
]

const mains: DishDef[] = [
  { id: 'bistec',   emoji: '🥩', requires: ['fork', 'knife'], es: 'Solomillo a la plancha', en: 'Grilled sirloin' },
  { id: 'pescado',  emoji: '🐟', requires: ['fork', 'knife'], es: 'Merluza al horno',       en: 'Baked hake'      },
  { id: 'pollo',    emoji: '🍗', requires: ['fork', 'knife'], es: 'Pollo asado',            en: 'Roast chicken'   },
  { id: 'chuleta',  emoji: '🥩', requires: ['fork', 'knife'], es: 'Chuleta de cerdo',       en: 'Pork chop'       },
]

const desserts: DishDef[] = [
  { id: 'flan',    emoji: '🍮', requires: ['teaspoon'], es: 'Flan casero',          en: 'Homemade flan'      },
  { id: 'tarta',   emoji: '🍰', requires: ['teaspoon'], es: 'Tarta de queso',       en: 'Cheesecake'         },
  { id: 'helado',  emoji: '🍨', requires: ['teaspoon'], es: 'Helado de vainilla',   en: 'Vanilla ice cream'  },
  { id: 'cafe',    emoji: '☕', requires: ['teaspoon'], es: 'Café solo',            en: 'Espresso'           },
  { id: 'mousse',  emoji: '🍫', requires: ['teaspoon'], es: 'Mousse de chocolate',  en: 'Chocolate mousse'   },
]

// ── Cover order ──────────────────────────────────────────────────
export interface CoverOrder {
  id:       string
  dishes:   DishDef[]
  required: CutleryType[]   // deduped union of all dish requirements
}

function makeOrder(id: string, dishes: DishDef[]): CoverOrder {
  const required = [...new Set(dishes.flatMap((d) => d.requires))] as CutleryType[]
  return { id, dishes, required }
}

// ── Cover generation ─────────────────────────────────────────────
// Easy:   2 covers, main course only (fork or fork+knife)
// Medium: 2 covers, 2 courses each (includes spoon and/or teaspoon)
// Hard:   3 covers, 2–3 courses, full range including teaspoon
export function generateCovers(difficulty: Difficulty): CoverOrder[] {
  if (difficulty === 'easy') {
    // Mix: one cover gets a pasta dish (fork only), the other a main (fork+knife)
    // Randomise which is which so it's not always the same order
    const a = makeOrder('cover-0', [pick(pasta)])
    const b = makeOrder('cover-1', [pick(mains)])
    return Math.random() < 0.5 ? [a, b] : [b, a]
  }

  if (difficulty === 'medium') {
    // Cover 1: soup + main (spoon + fork + knife)
    // Cover 2: main or pasta + dessert (fork+knife or fork, plus teaspoon)
    const c0 = makeOrder('cover-0', [pick(soups), pick(mains)])
    const c1 = makeOrder('cover-1', [pick([...pasta, ...mains]), pick(desserts)])
    return Math.random() < 0.5 ? [c0, c1] : [c1, c0]
  }

  // Hard: 3 covers, all four cutlery types appear across the table
  const c0 = makeOrder('cover-0', [pick(soups), pick(mains), pick(desserts)])
  const c1 = makeOrder('cover-1', [pick([...pasta, ...mains]), pick(desserts)])
  const c2 = makeOrder('cover-2', [pick(soups), pick([...pasta, ...mains])])
  return shuffle([c0, c1, c2])
}

// ── Count mode (Cuántos cubiertos) ───────────────────────────────

export interface OrderItem {
  dish: DishDef
  qty:  number
}

export interface ComandaData {
  dinerCount:  number
  items:       OrderItem[]                      // aggregated for display
  minCutlery:  Record<CutleryType, number>      // per-diner dedup (reuse allowed)
  maxCutlery:  Record<CutleryType, number>      // every piece counted separately
}

function emptyTypeCounts(): Record<CutleryType, number> {
  return { spoon: 0, fork: 0, knife: 0, teaspoon: 0 }
}

// Per-diner meals — returned meal arrays guarantee at least some ambiguity
// in medium/hard (a diner with pasta + main can reuse the fork).
function buildMeals(difficulty: Difficulty): DishDef[][] {
  if (difficulty === 'easy') {
    // 2 diners, 1 dish each — no reuse possible
    return shuffle([
      [pick([...soups, ...pasta])],
      [pick(mains)],
    ])
  }
  if (difficulty === 'medium') {
    // 3 diners — diner with pasta+main creates fork ambiguity
    return shuffle([
      [pick(soups), pick(mains)],
      [pick(pasta), pick(mains)],   // fork can be reused
      [pick(desserts)],
    ])
  }
  // Hard: 4 diners — multiple ambiguous diners
  return shuffle([
    [pick(soups), pick(pasta), pick(mains)],    // fork can be reused
    [pick(soups), pick(mains), pick(desserts)],
    [pick(pasta), pick(mains)],                 // fork can be reused
    [pick(mains), pick(desserts)],
  ])
}

export function generateComanda(difficulty: Difficulty): ComandaData {
  const meals = buildMeals(difficulty)

  // Aggregate dishes for display (group by dish.id, sum quantities)
  const byId = new Map<string, { dish: DishDef; qty: number }>()
  for (const meal of meals) {
    for (const dish of meal) {
      const entry = byId.get(dish.id)
      if (entry) entry.qty++
      else byId.set(dish.id, { dish, qty: 1 })
    }
  }
  const items: OrderItem[] = [...byId.values()]

  // min: per-diner type deduplication (reuse within a diner's meal)
  // max: every dish piece counted separately
  const minCutlery = emptyTypeCounts()
  const maxCutlery = emptyTypeCounts()
  for (const meal of meals) {
    const dinerTypes = new Set(meal.flatMap((d) => d.requires as CutleryType[]))
    for (const t of dinerTypes)                           minCutlery[t]++
    for (const dish of meal) for (const t of dish.requires as CutleryType[]) maxCutlery[t]++
  }

  return { dinerCount: meals.length, items, minCutlery, maxCutlery }
}
